import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Hash, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Button from '../ui/Button';

interface Contract {
  id: string;
  client_id: string;
  type: 'cold_calling' | 'mailing' | 'growth';
  billing_unit: 'days' | 'campaign' | 'package';
  signed_date: string | null;
  start_date: string;
  end_date: string | null;
  days_per_month: number | null;
  monthly_price: number | null;
  price_per_day: number | null;
  is_active: boolean;
  version: number;
  notes: string | null;
}

interface EditContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contractId: string, updates: Partial<Contract>) => Promise<void>;
  contract: Contract;
}

const EditContractModal: React.FC<EditContractModalProps> = ({ isOpen, onClose, onSave, contract }) => {
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    days_per_month: '',
    monthly_price: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && contract) {
      setFormData({
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        days_per_month: contract.days_per_month?.toString() || '',
        monthly_price: contract.monthly_price?.toString() || '',
        notes: contract.notes || '',
      });
      setError(null);
    }
  }, [isOpen, contract]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const updates: Partial<Contract> = {
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        days_per_month: formData.days_per_month ? parseInt(formData.days_per_month) : null,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : null,
        notes: formData.notes || null,
      };

      await onSave(contract.id, updates);
      onClose();
    } catch (err) {
      console.error('Error updating contract:', err);
      setError('Une erreur est survenue lors de la modification du contrat');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'cold_calling':
        return 'Cold Calling';
      case 'mailing':
        return 'Mailing';
      case 'growth':
        return 'Growth';
      default:
        return type;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <div>
              <h3 className="text-xl font-semibold text-white">Modifier le contrat</h3>
              <p className="text-sm text-gray-400 mt-1">{getTypeLabel(contract.type)} - Version {contract.version}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6">
            {error && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="inline h-4 w-4 mr-2" />
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="inline h-4 w-4 mr-2" />
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Hash className="inline h-4 w-4 mr-2" />
                    Jours par mois
                  </label>
                  <input
                    type="number"
                    value={formData.days_per_month}
                    onChange={(e) => setFormData({ ...formData, days_per_month: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
                    min="0"
                    step="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <DollarSign className="inline h-4 w-4 mr-2" />
                    Prix mensuel (€)
                  </label>
                  <input
                    type="number"
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#EE3195] focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FileText className="inline h-4 w-4 mr-2" />
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#EE3195] focus:border-transparent resize-none"
                  placeholder="Notes additionnelles..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-700">
              <Button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-[#EE3195] text-white rounded-lg hover:bg-[#d91e7f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditContractModal;
